// Authentication service
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// User type definitions
export interface User {
  id: string;
  username: string;
  email: string;
  role: string;
  lastLogin: string | null;
  createdAt: string;
}

export interface LoginResponse {
  message: string;
  user: User;
  token: string;
}

export interface AuthResponse {
  success: boolean;
  message?: string;
  error?: string;
}

class AuthService {
  // Store the token in localStorage
  setToken(token: string): void {
    localStorage.setItem('authToken', token);
  }

  // Get the token from localStorage
  getToken(): string | null {
    return localStorage.getItem('authToken');
  }

  // Remove the token from localStorage
  removeToken(): void {
    localStorage.removeItem('authToken');
  }

  // Store the user in localStorage
  setUser(user: User): void {
    localStorage.setItem('user', JSON.stringify(user));
  }

  // Get the user from localStorage
  getUser(): User | null {
    const user = localStorage.getItem('user');
    if (user) {
      return JSON.parse(user);
    }
    return null;
  }

  // Remove the user from localStorage
  removeUser(): void {
    localStorage.removeItem('user');
  }

  // Check if the user is authenticated
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  // Check if the user is an admin
  isAdmin(): boolean {
    const user = this.getUser();
    return user ? user.role === 'admin' : false;
  }

  // Configure axios with the token
  configureAxiosHeaders() {
    const token = this.getToken();
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }

  // Login
  async login(username: string, password: string): Promise<LoginResponse> {
    try {
      const response = await axios.post<LoginResponse>(`${API_URL}/auth/login`, {
        username,
        password
      });
      
      if (response.data && response.data.token) {
        this.setToken(response.data.token);
        this.setUser(response.data.user);
        this.configureAxiosHeaders();
      }
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data.error || 'Anmeldung fehlgeschlagen');
      }
      throw new Error('Anmeldung fehlgeschlagen');
    }
  }

  // Logout
  async logout(): Promise<void> {
    try {
      await axios.post(`${API_URL}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.removeToken();
      this.removeUser();
      delete axios.defaults.headers.common['Authorization'];
    }
  }

  // Get current user
  async getCurrentUser(): Promise<User> {
    try {
      const response = await axios.get<{ user: User }>(`${API_URL}/auth/me`);
      this.setUser(response.data.user);
      return response.data.user;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response && error.response.status === 401) {
        this.removeToken();
        this.removeUser();
      }
      throw error;
    }
  }

  // Request password reset
  async requestPasswordReset(email: string): Promise<AuthResponse> {
    try {
      const response = await axios.post<AuthResponse>(`${API_URL}/auth/reset-request`, {
        email
      });
      return { success: true, message: response.data.message };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return { success: false, error: error.response.data.error };
      }
      return { success: false, error: 'Fehler beim Anfordern des Passwort-Resets' };
    }
  }

  // Reset password
  async resetPassword(token: string, newPassword: string): Promise<AuthResponse> {
    try {
      const response = await axios.post<AuthResponse>(`${API_URL}/auth/reset-password`, {
        token,
        newPassword
      });
      return { success: true, message: response.data.message };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return { success: false, error: error.response.data.error };
      }
      return { success: false, error: 'Fehler beim Zurücksetzen des Passworts' };
    }
  }

  // Change password (for authenticated users)
  async changePassword(currentPassword: string, newPassword: string): Promise<AuthResponse> {
    try {
      const response = await axios.post<AuthResponse>(`${API_URL}/auth/change-password`, {
        currentPassword,
        newPassword
      });
      return { success: true, message: response.data.message };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return { success: false, error: error.response.data.error };
      }
      return { success: false, error: 'Fehler beim Ändern des Passworts' };
    }
  }

  // Initialize the auth service on app load
  initialize() {
    this.configureAxiosHeaders();
  }
}

export default new AuthService();